// Multi-manager buyer teams (WS4) — the team registry at /buyers/teams.
//
// The list is the whole set (the endpoint is unpaged), so `SubTable`'s own
// search / sort / paging chrome is truthful here: it is client chrome over a
// COMPLETE set, not a pager painted on top of an already-truncated one.
//
// Read-gated, not manage-gated: a specialist opens this to see which team they
// are on. Creating a team is gated on `BuyerTeams.Manage` — the DOOR, which F1
// now holds (§3.3). Editing a ROW additionally reads the row's own
// server-resolved `canManage` (an all-teams holder for every team, otherwise
// only a team the caller manages), and archive / unarchive stay all-teams-only
// (`Buyers.ManageAllTeams`) because the service keeps them so. Hiding is never
// the gate — the service re-checks every write — but a control that would 403
// must not render.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { extractErrorMessage } from "@/api/client";
import type {
  BuyerTeamSummaryDto,
  CreateBuyerTeamRequest,
  UpdateBuyerTeamRequest,
} from "@/api/buyerTeams";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { Textarea } from "@/components/ui/Select";
import {
  Button,
  FilterChips,
  Input,
  MissingValue,
  OverflowMenu,
  Skeleton,
  StatusBadge,
  SubTable,
  Timestamp,
  useToast,
  type SubTableColumn,
} from "@/design-system/components/ui";
import {
  useBuyerTeams,
  useCreateBuyerTeam,
  useSetBuyerTeamArchived,
  useUpdateBuyerTeam,
} from "@/hooks/buyers/useBuyerTeams";
import { useAuth } from "@/hooks/useAuth";
import { QueryErrorState } from "./queryStates";

export const BUYER_TEAMS_MANAGE_PERMISSION = "BuyerTeams.Manage";
/** Archive / unarchive stay all-teams-only server-side (§3.3). */
export const BUYER_TEAMS_ARCHIVE_PERMISSION = "Buyers.ManageAllTeams";
/**
 * 🔴 Naming SOMEBODY ELSE as the manager of a team you are CREATING is
 * all-teams-only, and the reason is that the server does not refuse it — it
 * SILENTLY SUBSTITUTES. `BuyerTeamService.CreateAsync` reads
 * `SeesEveryTeam() ? request.ManagerUserId : _currentUser.UserId`, so an F1 who
 * picks a colleague creates a team managed by THEMSELVES, is moved onto it as
 * its manager, and is told nothing. That is deliberate on the server (§3.3:
 * creating a team for somebody else and then being refused the edit hands you a
 * record you cannot open) — which makes the picker, not the rule, the thing
 * that has to go.
 *
 * <p>EDIT is untouched and the picker stays: `UpdateAsync` HONOURS
 * `ManagerUserId` for anyone who passes `RequireManagesAsync`, so handing the
 * title over is a real capability an F1 has. The asymmetry is the point — one
 * path substitutes, the other does not.</p>
 */
export const BUYER_TEAMS_NAME_OTHER_MANAGER_PERMISSION = "Buyers.ManageAllTeams";

/**
 * 🔴 The picker is the WHOLE assignable roster, deliberately un-narrowed.
 *
 * <p>The server validates that the user EXISTS and nothing else — it takes no
 * view on role. A role-derived shortlist here would therefore be a second
 * eligibility rule, free to disagree with the only one that is enforced, and
 * its failure mode is silent: a person the server would accept simply is not
 * in the list, with no way to say so. An empty list would also be a bootstrap
 * deadlock on a fresh environment, which is the defect the buyer-consultant
 * picker already shipped once.</p>
 */
const ANY_ASSIGNABLE_USER = "";

type Pending =
  | { kind: "create" }
  | { kind: "edit"; team: BuyerTeamSummaryDto }
  | { kind: "archive"; team: BuyerTeamSummaryDto; archive: boolean }
  | null;

export function BuyerTeamsPage() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = hasPermission(BUYER_TEAMS_MANAGE_PERMISSION);
  const canArchive = canManage && hasPermission(BUYER_TEAMS_ARCHIVE_PERMISSION);

  const [includeArchived, setIncludeArchived] = useState(false);
  const [pending, setPending] = useState<Pending>(null);

  const teams = useBuyerTeams(includeArchived);
  const setArchived = useSetBuyerTeamArchived();

  function confirmArchive() {
    if (pending?.kind !== "archive") return;
    const { team, archive } = pending;
    setArchived.mutate(
      { id: team.id, archived: archive },
      {
        onSuccess: () => {
          setPending(null);
          toast.success(`"${team.name}" ${archive ? "archived" : "unarchived"}.`);
        },
        onError: (e) => {
          setPending(null);
          toast.error(extractErrorMessage(e));
        },
      },
    );
  }

  const columns: SubTableColumn<BuyerTeamSummaryDto>[] = [
    {
      key: "name",
      header: "Team",
      sortAccessor: (t) => t.name,
      render: (t) => (
        <Link
          to={`/buyers/teams/${t.id}`}
          className="font-semibold text-gold-shadow hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {t.name}
        </Link>
      ),
    },
    {
      key: "manager",
      header: "Manager",
      sortAccessor: (t) => t.managerDisplayName ?? "",
      // A team with no manager resolves no caller scope server-side, so the
      // absence is worth reading as an absence rather than as a blank cell.
      render: (t) => t.managerDisplayName ?? <MissingValue />,
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (t) => t.status,
      // Open is the ordinary state and carries no pill — a badge on every row
      // says nothing. Archived is the exception, so it is the one that shows.
      render: (t) =>
        t.status === "Archived" ? (
          <StatusBadge tone="muted">Archived</StatusBadge>
        ) : (
          <span className="text-small text-gray-mid">Open</span>
        ),
    },
    {
      key: "members",
      header: "Members",
      align: "right",
      sortAccessor: (t) => t.memberCount,
      render: (t) => <span className="tabular-nums">{t.memberCount}</span>,
    },
    {
      key: "specialists",
      header: "Consultants",
      align: "right",
      sortAccessor: (t) => t.specialistCount,
      render: (t) => <span className="tabular-nums">{t.specialistCount}</span>,
    },
    {
      key: "created",
      header: "Created",
      sortAccessor: (t) => t.createdAt,
      render: (t) => <Timestamp value={t.createdAt} absolute className="text-gray-mid" />,
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            searchText: () => "",
            // The permission opens the column; the ROW's own `canManage` decides
            // whether this team is one the caller may edit. A "⋯" that opens
            // onto nothing is worse than no "⋯", so a row outside the caller's
            // scope renders no menu at all.
            render: (t: BuyerTeamSummaryDto) =>
              t.canManage === true ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <OverflowMenu
                    label={`Actions for ${t.name}`}
                    items={[
                      { label: "Edit", onSelect: () => setPending({ kind: "edit", team: t }) },
                      ...(canArchive
                        ? [
                            t.status === "Archived"
                              ? {
                                  label: "Unarchive",
                                  onSelect: () =>
                                    setPending({ kind: "archive", team: t, archive: false }),
                                }
                              : {
                                  label: "Archive",
                                  destructive: true,
                                  onSelect: () =>
                                    setPending({ kind: "archive", team: t, archive: true }),
                                },
                          ]
                        : []),
                    ]}
                  />
                </span>
              ) : null,
          } satisfies SubTableColumn<BuyerTeamSummaryDto>,
        ]
      : []),
  ];

  return (
    <div data-testid="buyer-teams-page">
      <PageHeader
        eyebrow="Buyers"
        title="Buyer teams"
        description="Each team has a manager, a roster of consultants, and its own assignment queue."
        actions={
          canManage ? (
            <Button size="sm" onClick={() => setPending({ kind: "create" })} data-testid="buyer-team-new">
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New team
            </Button>
          ) : undefined
        }
      />

      <div className="mb-space-4">
        <FilterChips
          label="Team status"
          activeId={includeArchived ? "all" : "open"}
          onChange={(id) => setIncludeArchived(id === "all")}
          chips={[
            { id: "open", label: "Open" },
            { id: "all", label: "Including archived" },
          ]}
        />
      </div>

      {teams.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : teams.isError ? (
        <QueryErrorState
          what="buyer teams"
          onRetry={() => void teams.refetch()}
          retrying={teams.isFetching}
        />
      ) : (
        <SubTable<BuyerTeamSummaryDto>
          caption="Buyer teams"
          data-testid="buyer-teams-table"
          rows={teams.data ?? []}
          rowKey={(t) => t.id}
          columns={columns}
          onRowClick={(t) => navigate(`/buyers/teams/${t.id}`)}
          searchPlaceholder="Search teams…"
          empty={{
            message: "No buyer teams yet.",
            // A door is DECLARED or omitted — never a control that cannot do
            // what it says. A reader without `BuyerTeams.Manage` gets prose
            // naming who can create one, not a button that would 403.
            door: canManage
              ? {
                  kind: "stamp",
                  label: "New team",
                  onClick: () => setPending({ kind: "create" }),
                }
              : {
                  kind: "derived",
                  explain: "A team is created by someone who manages the buyer org structure.",
                },
          }}
        />
      )}

      {(pending?.kind === "create" || pending?.kind === "edit") && (
        <TeamEditModal
          team={pending.kind === "edit" ? pending.team : undefined}
          onClose={() => setPending(null)}
          onSaved={(name, created) => {
            setPending(null);
            toast.success(`Team "${name}" ${created ? "created" : "updated"}.`);
          }}
        />
      )}

      {pending?.kind === "archive" && (
        <ConfirmModal
          open
          title={pending.archive ? "Archive team?" : "Unarchive team?"}
          message={
            pending.archive ? (
              <>
                Archive <strong>{pending.team.name}</strong>? It keeps its members, its leads
                and its history — it drops out of the picker and stops accepting new members.
              </>
            ) : (
              <>
                Unarchive <strong>{pending.team.name}</strong>? It returns to the picker and
                can take members again.
              </>
            )
          }
          confirmLabel={pending.archive ? "Archive" : "Unarchive"}
          destructive={pending.archive}
          onConfirm={confirmArchive}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

/** Create and rename share one form: the fields are identical and the only
 *  difference is which mutation runs. */
export function TeamEditModal({
  team,
  onClose,
  onSaved,
}: {
  team?: BuyerTeamSummaryDto;
  onClose: () => void;
  onSaved: (name: string, created: boolean) => void;
}) {
  const { hasPermission } = useAuth();
  // Create-only, and only for a caller whose pick the server will honour — see
  // BUYER_TEAMS_NAME_OTHER_MANAGER_PERMISSION. On edit the picker always shows.
  const mayNameAnotherManager =
    team !== undefined || hasPermission(BUYER_TEAMS_NAME_OTHER_MANAGER_PERMISSION);
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");
  const [managerUserId, setManagerUserId] = useState<string | null>(team?.managerUserId ?? null);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateBuyerTeam();
  const update = useUpdateBuyerTeam(team?.id ?? "");
  const saving = create.isPending || update.isPending;

  function submit() {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    const onError = (e: unknown) => setError(extractErrorMessage(e));

    if (team) {
      // FIND-12: the form hydrates every field, so it always sends every field
      // — "" is what CLEARS description / manager, and an omitted field would
      // read as "unchanged" and silently keep a manager the user just removed.
      const body: UpdateBuyerTeamRequest = {
        name: trimmed,
        description,
        managerUserId: managerUserId ?? "",
      };
      update.mutate(body, { onSuccess: () => onSaved(trimmed, false), onError });
      return;
    }

    const body: CreateBuyerTeamRequest = {
      name: trimmed,
      description: description || null,
      managerUserId,
    };
    create.mutate(body, { onSuccess: () => onSaved(trimmed, true), onError });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={team ? "Edit team" : "New team"}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={saving}
            disabled={!name.trim() || saving}
            onClick={submit}
            data-testid="buyer-team-save"
          >
            {team ? "Save" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3" data-testid="buyer-team-edit-modal">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Name</span>
          <Input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            data-testid="buyer-team-name-input"
          />
        </label>
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">
            Description (optional)
          </span>
          <Textarea value={description} rows={3} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Manager</span>
          {mayNameAnotherManager ? (
            <>
              <RoleFilteredUserPicker
                role={ANY_ASSIGNABLE_USER}
                value={managerUserId}
                onChange={setManagerUserId}
                placeholder="Unassigned"
                aria-label="Team manager"
              />
              {/* D5 #7 — the two things the server does with this field that a
                  reader cannot see from the picker: the manager is made a member,
                  and a manager of another team is refused. */}
              <span className="text-caption text-gray-mid" data-testid="buyer-team-manager-hint">
                The manager is also made a member of the team, so their queue resolves to it. A
                person who already manages another team cannot be named here unless they hold
                all-teams authority.
              </span>
            </>
          ) : (
            // The outcome stated, rather than a picker whose answer is thrown
            // away. Saying it here is the whole fix: the server's behaviour is
            // deliberate and unchanged.
            <span className="text-caption text-gray-mid" data-testid="buyer-team-manager-forced">
              You will manage this team, and be added to it as its manager so your queue
              resolves to it. Handing the title to somebody else is done from the team once it
              exists.
            </span>
          )}
        </div>
        {error && (
          <p className="text-label text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
